import { LoaderCircle } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import ReleaseNoteEmptyView from '@/components/manage-release-notes/release-note-empty-view';
import ReleaseNoteList from '@/components/manage-release-notes/release-note-list';
import StatusMessage from '@/components/shared/status-message';
import useGetReleaseNoteLists from '@/hooks/use-get-release-note-lists';
import { ERROR_FEEDBACK } from '@/utils/constants';
import { SEARCH_PARAMS_LIMIT, SEARCH_PARAMS_PAGE } from '@/utils/constants/queries';

const ReleaseNoteContent = ({
  onClick,
  onEdit,
}: {
  onClick: () => void;
  onEdit?: (id: string | number) => void;
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get(SEARCH_PARAMS_PAGE)) || 0;
  const limit = Number(searchParams.get(SEARCH_PARAMS_LIMIT)) || 10;

  const updateParams = (newPage: number, newLimit: number) => {
    setSearchParams(
      (prev) => {
        prev.set(SEARCH_PARAMS_PAGE, newPage.toString());
        prev.set(SEARCH_PARAMS_LIMIT, newLimit.toString());

        return prev;
      },
      { replace: true }
    );
  };

  const {
    data: releaseNoteLists,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetReleaseNoteLists({ page, limit });

  const { releases, count = 0 } = releaseNoteLists || {};

  useEffect(() => {
    if (!isLoading && !isFetching && releases?.length === 0 && page > 0) {
      updateParams(page - 1, limit);
    }
  }, [isLoading, isFetching, releases?.length, page]);

  if (isLoading || isFetching) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoaderCircle className="size-12 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <StatusMessage
          type="error"
          title={t(ERROR_FEEDBACK.general)}
          description={t(ERROR_FEEDBACK.release_note.api)}
          showReloadButton
          onRetry={refetch}
        />
      </div>
    );
  }

  const isEmpty = releases?.length === 0;

  if (isEmpty && page === 0) {
    return <ReleaseNoteEmptyView onClick={onClick} />;
  }

  return (
    <ReleaseNoteList
      releaseNoteLists={releases}
      onEdit={onEdit}
      totalCount={count}
      limit={limit}
      currentPage={page}
      onPageChange={(newPage) => updateParams(newPage, limit)}
      onLimitChange={(newLimit) => updateParams(0, newLimit)}
    />
  );
};

export default ReleaseNoteContent;
